import React, { useState, useEffect } from "react";
import {
  Box,
  Button,
  Dialog,
  DialogContent,
  TextField,
  Typography,
  Snackbar,
  IconButton,
  Stack,
  LinearProgress,
  Alert,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
} from "@mui/material";
import AddIcon from "@mui/icons-material/Add";
import UploadFileIcon from "@mui/icons-material/UploadFile";
import EditIcon from "@mui/icons-material/Edit";
import { DataGrid } from "@mui/x-data-grid";
import ModalDialogTitle from "../../common/ModalDialogTitle";
import ModalActions from "../../common/ModalActions";
import ConfirmDeleteDialog from "../../common/ConfirmDeleteDialog";
import * as XLSX from "xlsx";
import { db } from "../../../config/firebase";
import {
  collection,
  getDocs,
  addDoc,
  setDoc,
  doc,
  deleteDoc,
} from "firebase/firestore";
import CcpCpBreadcrumbs from "../../Layout/inc/CcpCpBreadcrumbs";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faFileImport,
  faFileDownload,
} from "@fortawesome/free-solid-svg-icons";
import useSchoolDropdown from "../CcpAdminBuildingMgt/useSchoolDropdown";

const initialForm = {
  highschool: "",
  name: "",
  tartanId: "",
  status: "",
  subject: "",
};

const CcpAdminInstructors = () => {
  const [data, setData] = useState([]);
  const [filter, setFilter] = useState({
    highschool: "",
    name: "",
    tartanId: "",
    status: "",
    subject: "",
  });
  const [filteredData, setFilteredData] = useState([]);
  const [modalOpen, setModalOpen] = useState(false);
  const [editIndex, setEditIndex] = useState(null);
  const [form, setForm] = useState(initialForm);
  const [snackbar, setSnackbar] = useState({
    open: false,
    message: "",
    severity: "success",
  });
  const [importStatus, setImportStatus] = useState({
    open: false,
    added: 0,
    updated: 0,
    total: 0,
    loading: false,
    duplicates: [], // Track duplicates in Excel upload
  });
  const [rowSelectionModel, setRowSelectionModel] = useState([]);
  const [confirmDeleteOpen, setConfirmDeleteOpen] = useState(false);

  const COLLECTION_NAME = "CcpInstructors";

  const { data: highschools, loading: highschoolsLoading } =
    useSchoolDropdown();

  useEffect(() => {
    const fetchData = async () => {
      const snapshot = await getDocs(collection(db, COLLECTION_NAME));
      let rows = snapshot.docs.map((d) => ({ id: d.id, ...d.data() }));
      // Sort by highschool alphabetically
      rows = rows.sort((a, b) =>
        (a.highschool || "").localeCompare(b.highschool || ""),
      );
      setData(rows);
    };
    fetchData();
  }, []);

  useEffect(() => {
    let filtered = data.filter(
      (row) =>
        (!filter.highschool ||
          (row.highschool || "")
            .toLowerCase()
            .includes(filter.highschool.toLowerCase())) &&
        (!filter.name ||
          (row.name || "").toLowerCase().includes(filter.name.toLowerCase())) &&
        (!filter.tartanId ||
          (row.tartanId || "")
            .toLowerCase()
            .includes(filter.tartanId.toLowerCase())) &&
        (!filter.status ||
          (row.status || "")
            .toLowerCase()
            .includes(filter.status.toLowerCase())) &&
        (!filter.subject ||
          (row.subject || "")
            .toLowerCase()
            .includes(filter.subject.toLowerCase())),
    );
    // Sort filtered results by highschool alphabetically
    filtered = filtered.sort((a, b) =>
      (a.highschool || "").localeCompare(b.highschool || ""),
    );
    setFilteredData(filtered);
  }, [data, filter]);

  const handleOpenAdd = () => {
    setEditIndex(null);
    setForm(initialForm);
    setModalOpen(true);
  };

  const handleOpenEdit = (params) => {
    const idx = data.findIndex((d) => d.tartanId === params.row.tartanId);
    setEditIndex(idx);
    setForm(params.row);
    setModalOpen(true);
  };

  const handleCloseModal = () => {
    setModalOpen(false);
  };

  const handleFormChange = (e) => {
    setForm({ ...form, [e.target.name]: e.target.value });
  };

  const handleSave = async () => {
    if (
      !form.highschool ||
      !form.name ||
      !form.tartanId ||
      !form.status ||
      !form.subject
    ) {
      setSnackbar({
        open: true,
        message: "All fields are required.",
        severity: "error",
      });
      return;
    }
    // Duplicate check for add form
    if (
      editIndex === null &&
      data.some(
        (d) =>
          (d.tartanId || "").trim().toLowerCase() ===
          form.tartanId.trim().toLowerCase(),
      )
    ) {
      setSnackbar({
        open: true,
        message: `An instructor with Tartan ID '${form.tartanId}' already exists.`,
        severity: "error",
      });
      return;
    }
    try {
      if (editIndex !== null) {
        const docId = data[editIndex].id;
        await setDoc(doc(db, COLLECTION_NAME, docId), form);
        const newData = [...data];
        newData[editIndex] = { ...form, id: docId };
        setData(newData);
        setSnackbar({
          open: true,
          message: "Instructor updated",
          severity: "success",
        });
      } else {
        const docRef = await addDoc(collection(db, COLLECTION_NAME), form);
        setData([...data, { ...form, id: docRef.id }]);
        setSnackbar({
          open: true,
          message: "Instructor added",
          severity: "success",
        });
      }
      setModalOpen(false);
    } catch (err) {
      setSnackbar({
        open: true,
        message: "Error saving to Firestore",
        severity: "error",
      });
    }
  };

  const handleDeleteInstructor = async () => {
    if (editIndex !== null) {
      const docId = data[editIndex].id;
      await deleteDoc(doc(db, COLLECTION_NAME, docId));
      const newData = data.filter((_, idx) => idx !== editIndex);
      setData(newData);
      setModalOpen(false);
      setSnackbar({
        open: true,
        message: "Instructor deleted",
        severity: "success",
      });
    }
  };

  const handleExcelUpload = (e) => {
    const file = e.target.files[0];
    if (!file) return;
    setImportStatus({
      open: true,
      added: 0,
      updated: 0,
      total: 0,
      loading: true,
      duplicates: [],
    });
    const reader = new FileReader();

    const processExcelRow = async (
      row,
      merged,
      counts,
      seenTartanIds,
      duplicates,
    ) => {
      const { added, updated } = counts;
      // Normalize tartanId
      const tartanId = (row.tartanId || "").trim().toLowerCase();
      if (!tartanId) return;
      // Duplicate check: skip if already in merged or seen in this upload
      if (
        merged.some(
          (d) => (d.tartanId || "").trim().toLowerCase() === tartanId,
        ) ||
        seenTartanIds.has(tartanId)
      ) {
        duplicates.push(row.tartanId);
        return;
      }
      seenTartanIds.add(tartanId);
      const idx = merged.findIndex((d) => d.tartanId === row.tartanId);
      if (idx > -1) {
        const docId = merged[idx].id;
        await setDoc(doc(db, COLLECTION_NAME, docId), row);
        merged[idx] = { ...merged[idx], ...row };
        counts.updated = updated + 1;
      } else {
        const docRef = await addDoc(collection(db, COLLECTION_NAME), row);
        merged.push({ ...row, id: docRef.id });
        counts.added = added + 1;
      }
    };

    reader.onload = async (evt) => {
      const bstr = evt.target.result;
      const wb = XLSX.read(bstr, { type: "binary" });
      const wsname = wb.SheetNames[0];
      const ws = wb.Sheets[wsname];
      const jsonData = XLSX.utils.sheet_to_json(ws);

      const merged = [...data];
      const counts = { added: 0, updated: 0 };
      const seenTartanIds = new Set();
      const duplicates = [];

      for (const row of jsonData) {
        await processExcelRow(row, merged, counts, seenTartanIds, duplicates);
        setImportStatus((s) => ({
          ...s,
          total: jsonData.length,
          added: counts.added,
          updated: counts.updated,
          duplicates,
        }));
      }

      setData(merged);
      setSnackbar({
        open: true,
        message: "Excel data imported",
        severity: "success",
      });
      setImportStatus((s) => ({ ...s, loading: false, duplicates }));
    };

    reader.readAsBinaryString(file);
  };

  const handleDeleteMultipleConfirmed = async () => {
    for (const id of rowSelectionModel) {
      await deleteDoc(doc(db, COLLECTION_NAME, id));
    }
    setData(data.filter((row) => !rowSelectionModel.includes(row.id)));
    setRowSelectionModel([]);
    setConfirmDeleteOpen(false);
    setSnackbar({
      open: true,
      message: "Selected instructors deleted",
      severity: "success",
    });
  };

  const handleOpenConfirmDelete = () => {
    if (!rowSelectionModel || rowSelectionModel.length === 0) return;
    setConfirmDeleteOpen(true);
  };

  const handleDownloadTemplate = () => {
    const headers = ["highschool", "name", "tartanId", "status", "subject"];
    const worksheet = XLSX.utils.aoa_to_sheet([headers]);
    const workbook = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(workbook, worksheet, "Template");
    XLSX.writeFile(workbook, "ccp-instructors-template.xlsx");
  };

  const rowsForGrid = filteredData.map((row) => ({
    ...row,
    id: String(row.id),
  }));

  const columns = [
    { field: "highschool", headerName: "High School", flex: 1, sortable: true },
    { field: "name", headerName: "Name", flex: 1, sortable: true },
    { field: "tartanId", headerName: "Tartan ID", flex: 1, sortable: true },
    { field: "status", headerName: "Status", flex: 1, sortable: true },
    { field: "subject", headerName: "Subject", flex: 1, sortable: true },
    // Removed actions column
  ];

  return (
    <Box sx={{ p: 3 }}>
      <Typography variant="h5" gutterBottom sx={{ mb: 1 }}>
        CCP Instructors Management
      </Typography>
      <CcpCpBreadcrumbs />
      <Stack
        direction={{ xs: "column", sm: "row" }}
        spacing={2}
        mb={2}
        alignItems="center"
        sx={{ mt: 3, mb: 3 }}
      >
        <TextField
          label="Filter High School"
          name="highschool"
          value={filter.highschool}
          onChange={(e) => setFilter({ ...filter, highschool: e.target.value })}
          size="small"
        />
        <TextField
          label="Filter Name"
          name="name"
          value={filter.name}
          onChange={(e) => setFilter({ ...filter, name: e.target.value })}
          size="small"
        />
        <TextField
          label="Filter Tartan ID"
          name="tartanId"
          value={filter.tartanId}
          onChange={(e) => setFilter({ ...filter, tartanId: e.target.value })}
          size="small"
        />
        <TextField
          label="Filter Status"
          name="status"
          value={filter.status}
          onChange={(e) => setFilter({ ...filter, status: e.target.value })}
          size="small"
        />
        <TextField
          label="Filter Subject"
          name="subject"
          value={filter.subject}
          onChange={(e) => setFilter({ ...filter, subject: e.target.value })}
          size="small"
        />
        <Button
          variant="contained"
          startIcon={<AddIcon />}
          onClick={handleOpenAdd}
        >
          Add
        </Button>
        <IconButton component="label" size="medium">
          <FontAwesomeIcon icon={faFileImport} color="#2e7d32" />
          <input
            type="file"
            accept=".xlsx, .xls"
            hidden
            onChange={handleExcelUpload}
          />
        </IconButton>
        <IconButton onClick={handleDownloadTemplate} size="medium">
          <FontAwesomeIcon icon={faFileDownload} color="#1976d2" />
        </IconButton>
        {rowSelectionModel && rowSelectionModel.length > 0 && (
          <Button
            variant="outlined"
            color="error"
            onClick={handleOpenConfirmDelete}
            sx={{ ml: 1 }}
          >
            Delete Selected
          </Button>
        )}
      </Stack>
      <Box
        sx={{
          height: 500,
          width: "100%",
          background: "#fff",
          borderRadius: 2,
          boxShadow: 1,
        }}
      >
        <DataGrid
          rows={rowsForGrid}
          columns={columns}
          pageSize={10}
          rowsPerPageOptions={[10, 20, 50]}
          disableSelectionOnClick
          onRowDoubleClick={handleOpenEdit}
          checkboxSelection
          rowSelectionModel={rowSelectionModel}
          onRowSelectionModelChange={(newSelectionModel) => {
            setRowSelectionModel(newSelectionModel);
          }}
        />
      </Box>
      <Dialog
        open={modalOpen}
        onClose={handleCloseModal}
        maxWidth="xs"
        fullWidth
      >
        <ModalDialogTitle onClose={handleCloseModal}>
          {editIndex !== null ? "Edit Instructor" : "Add Instructor"}
        </ModalDialogTitle>
        <DialogContent>
          <Stack spacing={2} mt={1}>
            {/* High School dropdown */}
            <FormControl fullWidth required sx={{ mb: 2 }}>
              <InputLabel>High School</InputLabel>
              <Select
                label="High School"
                name="highschool"
                value={form.highschool}
                onChange={handleFormChange}
                disabled={highschoolsLoading}
              >
                {/* If the current value is not in the dropdown, add it as a MenuItem so it displays */}
                {form.highschool &&
                  !highschools.some(
                    (school) => school.school_name === form.highschool,
                  ) && (
                    <MenuItem value={form.highschool}>
                      {form.highschool}
                    </MenuItem>
                  )}
                {highschools.map((school) => (
                  <MenuItem key={school.school_name} value={school.school_name}>
                    {school.school_name}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
            <TextField
              label="Name"
              name="name"
              value={form.name}
              onChange={handleFormChange}
              required
              fullWidth
            />
            <TextField
              label="Tartan ID"
              name="tartanId"
              value={form.tartanId}
              onChange={handleFormChange}
              required
              fullWidth
            />
            <TextField
              label="Status"
              name="status"
              value={form.status}
              onChange={handleFormChange}
              required
              fullWidth
            />
            <TextField
              label="Subject"
              name="subject"
              value={form.subject}
              onChange={handleFormChange}
              required
              fullWidth
            />
          </Stack>
        </DialogContent>
        <ModalActions
          onSave={handleSave}
          onDelete={editIndex !== null ? handleDeleteInstructor : undefined}
        />
      </Dialog>
      <ConfirmDeleteDialog
        open={confirmDeleteOpen}
        onClose={() => setConfirmDeleteOpen(false)}
        onConfirm={handleDeleteMultipleConfirmed}
        title="Delete Selected Instructors"
        message={`Are you sure you want to delete ${rowSelectionModel.length} selected instructor${
          rowSelectionModel.length === 1 ? "" : "s"
        }? This action cannot be undone.`}
      />
      <Snackbar
        open={snackbar.open}
        autoHideDuration={3000}
        onClose={() => setSnackbar({ ...snackbar, open: false })}
        message={snackbar.message}
      />
      {importStatus.open && (
        <Box sx={{ mt: 2 }}>
          {importStatus.loading && <LinearProgress />}
          <Alert severity="info" sx={{ mt: 1 }}>
            Importing Excel:{" "}
            {importStatus.loading ? "Processing..." : "Complete."} <br />
            Added: {importStatus.added} &nbsp; Updated: {importStatus.updated}{" "}
            &nbsp; Total: {importStatus.total}
            {importStatus.duplicates && importStatus.duplicates.length > 0 && (
              <>
                <br />
                Duplicates skipped: {importStatus.duplicates.join(", ")}
              </>
            )}
          </Alert>
        </Box>
      )}
    </Box>
  );
};

export default CcpAdminInstructors;
